import React from 'react';
import { Badge } from '../../ui/badge';
import { Button } from '../../ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../ui/table';
import { Loader2, Pencil, History, Trash2, Eye, EyeOff, Sparkles, FilePlus } from 'lucide-react';

export default function ContentTable({
  loading,
  contents,
  onEdit,
  onViewVersions,
  onDelete,
  onSeedDefaults,
  onAddNew,
}) {
  if (loading) {
    return (
      <div className="flex justify-center py-8">
        <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (contents.length === 0) {
    return (
      <div className="text-center py-12 border-2 border-dashed rounded-lg">
        <FilePlus className="w-12 h-12 mx-auto mb-3 text-muted-foreground opacity-50" />
        <p className="font-medium mb-1">No content blocks yet</p>
        <p className="text-sm text-muted-foreground mb-5 max-w-md mx-auto">
          Content blocks let you edit hard-coded text across the site (like the homepage
          tagline or about page) without deploying code. Seed the defaults to start with
          the 4 core blocks.
        </p>
        <div className="flex gap-2 justify-center">
          {onSeedDefaults && (
            <Button onClick={onSeedDefaults} data-testid="empty-seed-defaults">
              <Sparkles className="w-4 h-4 mr-2" />
              Seed Defaults
            </Button>
          )}
          {onAddNew && (
            <Button variant="outline" onClick={onAddNew}>
              <FilePlus className="w-4 h-4 mr-2" />
              Add Block
            </Button>
          )}
        </div>
      </div>
    );
  }

  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Key</TableHead>
          <TableHead>Title</TableHead>
          <TableHead>Category</TableHead>
          <TableHead>Type</TableHead>
          <TableHead>Status</TableHead>
          <TableHead>Updated</TableHead>
          <TableHead className="text-right">Actions</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {contents.map((content) => (
          <TableRow key={content.id}>
            <TableCell className="font-mono text-sm">{content.key}</TableCell>
            <TableCell className="font-medium">{content.title}</TableCell>
            <TableCell>
              <Badge variant="outline">{content.category || 'none'}</Badge>
            </TableCell>
            <TableCell>{content.content_type}</TableCell>
            <TableCell>
              {content.is_published ? (
                <Badge className="bg-green-100 text-green-700">
                  <Eye className="w-3 h-3 mr-1" />
                  Published
                </Badge>
              ) : (
                <Badge variant="secondary">
                  <EyeOff className="w-3 h-3 mr-1" />
                  Draft
                </Badge>
              )}
            </TableCell>
            <TableCell className="text-sm text-muted-foreground">
              {new Date(content.updated_at).toLocaleDateString()}
            </TableCell>
            <TableCell className="text-right">
              <div className="flex justify-end gap-1">
                <Button variant="ghost" size="sm" onClick={() => onEdit(content)}>
                  <Pencil className="w-4 h-4" />
                </Button>
                <Button variant="ghost" size="sm" onClick={() => onViewVersions(content.id)}>
                  <History className="w-4 h-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-red-600 hover:text-red-700"
                  onClick={() => onDelete(content.id)}
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}
